import { X, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useBuyer } from "@/contexts/BuyerContext";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { getTrialCountdown, formatTrialCountdown } from "@/utils/trialCountdown";

const SellerSubscriptionBanner = () => {
  const { subscriptionStatus, subscriptionEnd } = useSubscription();
  const { isBuyer } = useBuyer();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [isDismissed, setIsDismissed] = useState(false);
  const [countdown, setCountdown] = useState(getTrialCountdown(subscriptionEnd));

  useEffect(() => {
    setCountdown(getTrialCountdown(subscriptionEnd));
    
    const interval = setInterval(() => {
      setCountdown(getTrialCountdown(subscriptionEnd));
    }, 60000);
    
    return () => clearInterval(interval);
  }, [subscriptionEnd]);

  // Only show for sellers (not buyers, has seller profile) with trial or expired status
  // Don't show for sponsored users
  const isSeller = !isBuyer && profile;
  if (!isSeller || subscriptionStatus === 'sponsored' || !['trial', 'expired'].includes(subscriptionStatus) || isDismissed) {
    return null;
  }

  const isExpired = subscriptionStatus === 'expired';

  const handleUpgrade = () => {
    navigate('/seller/upgrade');
  };

  const handleDismiss = () => {
    if (!isExpired) { // Only allow dismissing for trial users
      setIsDismissed(true);
    }
  };

  return (
    <div className="bg-gradient-to-r from-godyspo-gold/10 to-godyspo-gold/5 border-b border-godyspo-gold/20">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              {isExpired ? (
                <div>
                  <p className="text-white font-medium">
                    Your free trial has expired
                  </p>
                  <p className="text-white/80 text-sm">
                    Upgrade to Shark Plan to continue selling deals
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-white font-medium flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Free Trial Active
                    {!countdown.isExpiredOrInvalid && (
                      <span className={cn(
                        "font-semibold",
                        countdown.daysRemaining > 7 && "text-green-400",
                        countdown.daysRemaining > 3 && countdown.daysRemaining <= 7 && "text-yellow-400",
                        countdown.daysRemaining <= 3 && "text-red-400"
                      )}>
                        - {formatTrialCountdown(countdown)}
                      </span>
                    )}
                  </p>
                  <p className="text-white/80 text-sm">
                    Upgrade to Shark Plan for just <span className="line-through opacity-70">$68</span> $34.99/month
                  </p>
                </div>
              )}
            </div>
            <Button
              onClick={handleUpgrade}
              className="bg-godyspo-gold text-black hover:bg-godyspo-gold/90 font-semibold"
            >
              Upgrade Now
            </Button>
          </div>
          {!isExpired && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDismiss}
              className="text-white/60 hover:text-white hover:bg-white/10"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerSubscriptionBanner;